import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import CreateDealerModal from './CreateDealerModal';
import CreateManagerModal from './CreateManagerModal';
import PasswordDisplayModal from './PasswordDisplayModal';
import ViewUserModal from './ViewUserModal';
import EditDealerModal from './EditDealerModal';
import EditManagerModal from './EditManagerModal';
import './UserManagementView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type UserRole = 'corporate' | 'regional' | 'district' | 'dealer';

interface User {
  user_id: number;
  username: string;
  email?: string;
  role: UserRole;
  dealer_id?: string;
  dealer_name?: string;
  manager_name?: string;
  manages?: string[];
  reports_to?: string;
  is_active: boolean;
  created_at?: string;
  last_login?: string;
  login_count?: number;
  priority_level?: 'High' | 'Medium' | 'Low';
}

interface UsersResponse {
  success: boolean;
  users: User[];
  total: number;
  page: number;
  per_page?: number;
  limit?: number;
  pages?: number;  // Backend returns 'pages'
  total_pages?: number;  // Old field name (for backwards compatibility)
}

interface UserManagementViewProps {
  preAppliedFilters?: {
    reports_to?: string;      // For regional managers filtering their districts
    role?: UserRole;          // For district managers showing only dealers
    manager?: string;         // For filtering by manager name
  };
  disableFilters?: string[];  // Array of filter names to disable
  scopeMessage?: string;      // Message to show users about their data scope
  currentUserRole?: UserRole | 'admin';  // Current user's role to determine edit permissions
}

const UserManagementView: React.FC<UserManagementViewProps> = ({
  preAppliedFilters,
  disableFilters = [],
  scopeMessage,
  currentUserRole
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if current user can edit priority levels (only admin and corporate)
  const canEditPriority = currentUserRole === 'admin' || currentUserRole === 'corporate';

  // Filters
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [regionalManagerFilter, setRegionalManagerFilter] = useState<string>('all'); // Phase 2.2.5
  const [inactivityFilter, setInactivityFilter] = useState<string>('all'); // Phase 2.2.5
  const [regionalManagers, setRegionalManagers] = useState<any[]>([]); // Phase 2.2.5

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const perPage = 50;

  // Modals
  const [showCreateDealerModal, setShowCreateDealerModal] = useState(false);
  const [showCreateManagerModal, setShowCreateManagerModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditDealerModal, setShowEditDealerModal] = useState(false);
  const [showEditManagerModal, setShowEditManagerModal] = useState(false);
  const [editDealerId, setEditDealerId] = useState<string | null>(null);
  const [editManagerId, setEditManagerId] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, roleFilter, statusFilter, regionalManagerFilter, inactivityFilter]);

  useEffect(() => {
    // Phase 2.2.5: Load regional managers for dropdown
    const loadRegionalManagers = async () => {
      try {
        const data = await adminAPI.getRegionalManagers();
        if (data.success) {
          setRegionalManagers(data.regional_managers);
        }
      } catch (err) {
        console.error('Failed to load regional managers:', err);
      }
    };
    loadRegionalManagers();
  }, []);

  // Apply pre-filters on mount (for hierarchical access control)
  useEffect(() => {
    if (preAppliedFilters) {
      if (preAppliedFilters.reports_to) {
        setRegionalManagerFilter(preAppliedFilters.reports_to);
      }
      if (preAppliedFilters.role) {
        setRoleFilter(preAppliedFilters.role);
      }
    }
  }, [preAppliedFilters]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        per_page: perPage,
      };

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Phase 2.2.5: Regional manager filter (for district managers)
      if (regionalManagerFilter !== 'all') {
        params.reports_to = regionalManagerFilter;
      }

      // Phase 2.2.5: Inactivity filter
      if (inactivityFilter !== 'all') {
        params.inactive_days = parseInt(inactivityFilter);
      }

      const data: UsersResponse = await adminAPI.getAllUsers(params);

      if (data.success) {
        setUsers(data.users);
        setTotalPages(data.pages || 1);  // Backend returns 'pages', not 'total_pages'
        setTotalUsers(data.total);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (err: any) {
      console.error('Fetch users error:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatRelativeTime = (isoString?: string): string => {
    if (!isoString) return 'Never';
    const ms = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  };

  const getRoleBadgeClass = (role: UserRole): string => {
    const roleClasses: Record<UserRole, string> = {
      corporate: 'role-badge-corporate',
      regional: 'role-badge-regional',
      district: 'role-badge-district',
      dealer: 'role-badge-dealer',
    };
    return roleClasses[role];
  };

  const getRoleLabel = (role: UserRole): string => {
    const roleLabels: Record<UserRole, string> = {
      corporate: 'Corporate',
      regional: 'Regional Manager',
      district: 'District Manager',
      dealer: 'Dealer',
    };
    return roleLabels[role];
  };

  const handleResetPassword = async (userId: number, username: string) => {
    if (!window.confirm(`Reset password for ${username}?`)) {
      return;
    }

    try {
      const data = await adminAPI.resetPassword(userId);

      if (data.success) {
        alert(`Password reset successfully!\n\nNew password: ${data.new_password}\n\nPlease save this password securely. It will not be shown again.`);
      } else {
        alert(`Failed to reset password: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error resetting password: ${err.message}`);
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEditUser = (user: User) => {
    if (user.role === 'dealer' && user.dealer_id) {
      setEditDealerId(user.dealer_id);
      setShowEditDealerModal(true);
    } else if (user.role === 'district' || user.role === 'regional') {
      setEditManagerId(user.user_id);
      setShowEditManagerModal(true);
    } else {
      alert('Corporate users cannot be edited through this interface');
    }
  };

  const handleDealerSuccess = (result: any) => {
    // Show password modal with dealer creation results
    setPasswordData({
      username: result.username,
      password: result.password,
      userType: 'dealer',
      coordinates: result.coordinates,
      radiusZips: result.radius_zips,
    });
    setShowPasswordModal(true);
    // Refresh user list
    fetchUsers();
  };

  const handleManagerSuccess = (result: any) => {
    // Show password modal with manager creation results
    setPasswordData({
      username: result.username,
      password: result.password,
      userType: 'manager',
    });
    setShowPasswordModal(true);
    // Refresh user list
    fetchUsers();
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.username}? This will soft-delete the user.`)) {
      return;
    }

    try {
      let data;
      if (user.role === 'dealer' && user.dealer_id) {
        data = await adminAPI.deleteDealer(user.dealer_id);
      } else {
        data = await adminAPI.deleteManager(user.user_id);
      }

      if (data.success) {
        alert('User deleted successfully');
        fetchUsers();
      } else {
        alert(`Failed to delete user: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error deleting user: ${err.message}`);
    }
  };

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.is_active ? 'inactive' : 'active';
    if (!window.confirm(`Change ${user.username} status to ${newStatus}?`)) {
      return;
    }

    try {
      const data = await adminAPI.toggleUserStatus(user.user_id);

      if (data.success) {
        alert(`User status changed to ${data.status}`);
        fetchUsers();
      } else {
        alert(`Failed to toggle status: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error toggling status: ${err.message}`);
    }
  };

  const handlePriorityChange = async (dealerId: string, priorityLevel: 'High' | 'Medium' | 'Low') => {
    try {
      const data = await adminAPI.updateDealerPriority(dealerId, priorityLevel);

      if (data.success) {
        // Update local state immediately for better UX
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.dealer_id === dealerId ? { ...u, priority_level: priorityLevel } : u
          )
        );
      } else {
        alert(`Failed to update priority: ${data.error}`);
      }
    } catch (err: any) {
      console.error('Error updating priority:', err);
      alert(`Error updating priority: ${err.message}`);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch ALL users matching current filters (not just current page)
      const params: any = {
        page: 1,
        per_page: 10000, // Get all matching users
      };

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Phase 2.2.5: Include new filters in CSV export
      if (regionalManagerFilter !== 'all') {
        params.reports_to = regionalManagerFilter;
      }

      if (inactivityFilter !== 'all') {
        params.inactive_days = parseInt(inactivityFilter);
      }

      const data: UsersResponse = await adminAPI.getAllUsers(params);

      if (!data.success || data.users.length === 0) {
        alert('No users to export');
        return;
      }

      // For dealers, fetch full details with address/coordinates
      const exportUsers = await Promise.all(
        data.users.map(async (user) => {
          if (user.role === 'dealer' && user.dealer_id) {
            try {
              const dealerData = await adminAPI.getDealer(user.dealer_id);
              return { ...user, ...dealerData };
            } catch {
              return user;
            }
          }
          return user;
        })
      );

      // Build CSV content
      const headers = [
        'User ID',
        'Username',
        'Email',
        'Role',
        'Status',
        'Dealer ID',
        'Dealer Name',
        'Priority Level',
        'Address',
        'City',
        'State',
        'ZIP',
        'Latitude',
        'Longitude',
        'District Manager',
        'Regional Manager',
        'Manager Name',
        'Reports To',
        'Manages Count',
        'Created At',
        'Last Login',
      ];

      const csvRows = [headers.join(',')];

      exportUsers.forEach((user) => {
        const row = [
          user.user_id,
          `"${user.username || ''}"`,
          `"${user.email || ''}"`,
          user.role,
          user.is_active ? 'Active' : 'Inactive',
          user.dealer_id || '',
          `"${user.dealer_name || ''}"`,
          user.priority_level || '',
          `"${user.address || ''}"`,
          `"${user.city || ''}"`,
          user.state || '',
          user.zip || '',
          user.latitude || '',
          user.longitude || '',
          `"${user.district_manager || ''}"`,
          `"${user.regional_manager || ''}"`,
          `"${user.manager_name || ''}"`,
          `"${user.reports_to || ''}"`,
          user.manages?.length || '',
          user.created_at || '',
          user.last_login || '',
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `gravely_users_export_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`Successfully exported ${exportUsers.length} users to ${filename}`);
    } catch (err: any) {
      console.error('Error exporting CSV:', err);
      alert(`Error exporting CSV: ${err.message}`);
    }
  };

  return (
    <div className="user-management-view">
      <div className="view-header">
        <h2>User Management</h2>
        <p className="view-subtitle">
          Manage all users across the system - from corporate to dealers
        </p>
      </div>

      {/* Hierarchical Scope Message */}
      {scopeMessage && (
        <div className="scope-message-banner">
          <span className="material-icons">info</span>
          <span>{scopeMessage}</span>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Role</label>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as UserRole | 'all');
                setCurrentPage(1);
              }}
              className="filter-select"
              disabled={disableFilters.includes('role')}
            >
              <option value="all">All Roles</option>
              <option value="corporate">Corporate</option>
              <option value="regional">Regional Manager</option>
              <option value="district">District Manager</option>
              <option value="dealer">Dealer</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setCurrentPage(1);
              }}
              className="filter-select"
              disabled={disableFilters.includes('status')}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Phase 2.2.5: Regional Manager Filter (for district managers) */}
          {roleFilter === 'district' && (
            <div className="filter-group">
              <label>Regional Manager</label>
              <select
                value={regionalManagerFilter}
                onChange={(e) => {
                  setRegionalManagerFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
                disabled={disableFilters.includes('regional_manager')}
              >
                <option value="all">All Regional Managers</option>
                {regionalManagers.map((rm) => (
                  <option key={rm.user_id} value={rm.manager_name}>
                    {rm.manager_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Phase 2.2.5: Inactivity Filter */}
          <div className="filter-group">
            <label>User Activity</label>
            <select
              value={inactivityFilter}
              onChange={(e) => {
                setInactivityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              disabled={disableFilters.includes('inactivity')}
            >
              <option value="all">All Users</option>
              <option value="30">Inactive &gt; 30 days</option>
              <option value="60">Inactive &gt; 60 days</option>
              <option value="90">Inactive &gt; 90 days</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              <button onClick={handleSearch} className="search-button">
                <span className="material-icons">search</span>
              </button>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="btn-secondary" onClick={handleExportCSV} title="Export current view to CSV">
            <span className="material-icons">download</span>
            Export CSV
          </button>
          <button className="btn-primary" onClick={() => setShowCreateDealerModal(true)}>
            <span className="material-icons">person_add</span>
            Add Dealer
          </button>
          <button className="btn-primary" onClick={() => setShowCreateManagerModal(true)}>
            <span className="material-icons">group_add</span>
            Add Manager
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <span className="results-text">
          Showing {users.length} of {totalUsers} users
        </span>
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <div className="error-message">
            <span className="material-icons">error</span>
            {error}
          </div>
          <button onClick={fetchUsers} className="btn-secondary">
            Retry
          </button>
        </div>
      ) : users.length === 0 ? (
        <div className="no-results">
          <span className="material-icons">search_off</span>
          <p>No users found matching your criteria</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Username</th>
                <th>Role</th>
                <th>Email</th>
                <th>Dealer/Manager Info</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id} className={!user.is_active ? 'inactive-row' : ''}>
                  <td>{user.user_id}</td>
                  <td>
                    <div className="username-cell">
                      <span className="material-icons user-icon">
                        {user.role === 'dealer' ? 'store' : 'person'}
                      </span>
                      {user.username}
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>{user.email || '-'}</td>
                  <td>
                    {user.role === 'dealer' && (
                      <div className="info-cell">
                        <div><strong>ID:</strong> {user.dealer_id}</div>
                        {user.dealer_name && <div><strong>Name:</strong> {user.dealer_name}</div>}
                      </div>
                    )}
                    {(user.role === 'district' || user.role === 'regional') && (
                      <div className="info-cell">
                        <div><strong>Name:</strong> {user.manager_name}</div>
                        {user.manages && Array.isArray(user.manages) && user.manages.length > 0 && (
                          <div><strong>Manages:</strong> {user.manages.length} {user.role === 'district' ? 'dealers' : 'districts'}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {user.role === 'dealer' && user.dealer_id ? (
                      canEditPriority ? (
                        <select
                          value={user.priority_level || ''}
                          onChange={(e) => handlePriorityChange(user.dealer_id!, e.target.value as 'High' | 'Medium' | 'Low')}
                          className={`priority-select priority-${(user.priority_level || '').toLowerCase()}`}
                        >
                          <option value="">Not Set</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      ) : (
                        <span className={`priority-badge priority-${(user.priority_level || 'notset').toLowerCase()}`}>
                          {user.priority_level || 'Not Set'}
                        </span>
                      )
                    ) : (
                      <span className="priority-na">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="last-login-cell">
                      <span className="last-login-time">{formatRelativeTime(user.last_login)}</span>
                      {user.login_count != null && user.login_count > 0 && (
                        <span className="login-count-badge" title={`${user.login_count} total logins`}>
                          {user.login_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        className="icon-button"
                        onClick={() => handleViewUser(user)}
                        title="View Details"
                      >
                        <span className="material-icons">visibility</span>
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEditUser(user)}
                        title="Edit User"
                      >
                        <span className="material-icons">edit</span>
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleResetPassword(user.user_id, user.username)}
                        title="Reset Password"
                      >
                        <span className="material-icons">lock_reset</span>
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleToggleStatus(user)}
                        title={user.is_active ? 'Deactivate User' : 'Activate User'}
                      >
                        <span className="material-icons">
                          {user.is_active ? 'toggle_on' : 'toggle_off'}
                        </span>
                      </button>
                      <button
                        className="icon-button delete-button"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete User"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <span className="material-icons">first_page</span>
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <span className="material-icons">chevron_left</span>
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <span className="material-icons">chevron_right</span>
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <span className="material-icons">last_page</span>
          </button>
        </div>
      )}

      {/* Modals */}
      <CreateDealerModal
        isOpen={showCreateDealerModal}
        onClose={() => setShowCreateDealerModal(false)}
        onSuccess={handleDealerSuccess}
      />

      <CreateManagerModal
        isOpen={showCreateManagerModal}
        onClose={() => setShowCreateManagerModal(false)}
        onSuccess={handleManagerSuccess}
      />

      {passwordData && (
        <PasswordDisplayModal
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setPasswordData(null);
          }}
          username={passwordData.username}
          password={passwordData.password}
          userType={passwordData.userType}
          coordinates={passwordData.coordinates}
          radiusZips={passwordData.radiusZips}
        />
      )}

      {selectedUser && (
        <ViewUserModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
        />
      )}

      {editDealerId && (
        <EditDealerModal
          isOpen={showEditDealerModal}
          onClose={() => {
            setShowEditDealerModal(false);
            setEditDealerId(null);
          }}
          onSuccess={() => {
            fetchUsers();
          }}
          dealerId={editDealerId}
        />
      )}

      {editManagerId && (
        <EditManagerModal
          isOpen={showEditManagerModal}
          onClose={() => {
            setShowEditManagerModal(false);
            setEditManagerId(null);
          }}
          onSuccess={() => {
            fetchUsers();
          }}
          managerId={editManagerId}
        />
      )}
    </div>
  );
};

export default UserManagementView;
